import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import { useProfile } from "../contexts/ProfileContext";
import { usePageTracking } from "../hooks/useAnalytics";

const FALLBACK_IMAGE = "/assets/pixley-logo.png";
const DEFAULT_NUM_CHARACTERS = 2;
const TEMPLATE_STAR_COST = 100;

const TemplateDetailPage = () => {
  usePageTracking("Template Detail");
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();
  const { user, updateUser } = useAuth();
  const { apiClient } = useAPI();
  const { currentProfile } = useProfile();

  const [template, setTemplate] = useState(location.state?.template || null);
  const [characters, setCharacters] = useState([]);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState([]);
  const [isTemplateLoading, setIsTemplateLoading] = useState(!location.state?.template);
  const [isCharactersLoading, setIsCharactersLoading] = useState(true);
  const [isCreatingStory, setIsCreatingStory] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [charactersError, setCharactersError] = useState("");

  const selectedIdSet = useMemo(
    () => new Set(selectedCharacterIds),
    [selectedCharacterIds]
  );

  const normalizeCharacters = useCallback((rawCharacters) => {
    if (!Array.isArray(rawCharacters)) return [];

    return rawCharacters
      .map((character) => ({
        id: character?.id,
        name: character?.name || "Unnamed Character",
        avatar_url:
          character?.avatar_url ||
          character?.animated_image_url ||
          character?.handdrawn_image_url ||
          character?.image_url ||
          character?.animated_image ||
          character?.handdrawn_image ||
          null,
      }))
      .filter((character) => character?.id !== undefined && character?.id !== null);
  }, []);

  const loadTemplate = useCallback(async () => {
    if (!apiClient || !templateId) return;

    setTemplateError("");
    setIsTemplateLoading(true);

    try {
      const response = await apiClient.getStoryTemplates();
      const isSuccess =
        response?.status === true || response?.status === "success";
      if (!isSuccess) {
        throw new Error(response?.error || response?.detail || "Failed to load template");
      }

      const categoriesObject = response?.data;
      if (
        !categoriesObject ||
        typeof categoriesObject !== "object" ||
        Array.isArray(categoriesObject)
      ) {
        throw new Error("Invalid template response format");
      }

      const allTemplates = Object.entries(categoriesObject).flatMap(
        ([categoryTitle, stories]) =>
          Array.isArray(stories)
            ? stories.map((story) => ({
                ...story,
                category: story?.category || categoryTitle,
              }))
            : []
      );

      const matchedTemplate = allTemplates.find(
        (story) => String(story?.id) === String(templateId)
      );

      if (!matchedTemplate) {
        throw new Error("Template not found");
      }

      setTemplate(matchedTemplate);
    } catch (error) {
      setTemplateError(
        error?.message || "We couldn't load this template right now."
      );
    } finally {
      setIsTemplateLoading(false);
    }
  }, [apiClient, templateId]);

  const loadCharacters = useCallback(async () => {
    if (!apiClient || !user?.id) return;

    setCharactersError("");
    setIsCharactersLoading(true);

    try {
      // Match CreateStory flow exactly: profile-aware characters endpoint.
      const charactersResponse = await apiClient.getUserCharacters(
        user.id,
        currentProfile?.id
      );
      const isCharactersSuccess =
        charactersResponse?.status === true ||
        charactersResponse?.status === "success";
      if (!isCharactersSuccess) {
        throw new Error(
          charactersResponse?.error ||
            charactersResponse?.detail ||
            "Failed to load characters"
        );
      }

      setCharacters(normalizeCharacters(charactersResponse?.data));
    } catch (error) {
      setCharactersError(
        error?.message || "We couldn't load your characters right now."
      );
      setCharacters([]);
    } finally {
      setIsCharactersLoading(false);
    }
  }, [apiClient, currentProfile?.id, normalizeCharacters, user?.id]);

  useEffect(() => {
    if (location.state?.template) {
      setTemplate(location.state.template);
      setIsTemplateLoading(false);
      return;
    }
    loadTemplate();
  }, [loadTemplate, location.state]);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  const maxCharacters = template?.num_characters || DEFAULT_NUM_CHARACTERS;

  const toggleCharacterSelection = (characterId) => {
    const id = String(characterId);
    setSelectedCharacterIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((value) => value !== id);
      }
      if (prev.length >= maxCharacters) {
        toast.error(`This story needs exactly ${maxCharacters} character${maxCharacters === 1 ? '' : 's'}`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const atSelectionLimit = selectedCharacterIds.length >= maxCharacters;

  const handleCreateStory = async () => {
    if (!apiClient || !user?.id || !template?.id || selectedCharacterIds.length !== maxCharacters) {
      return;
    }

    // Check and deduct stars
    const currentStars = user?.pixley_stars ?? 0;
    if (currentStars < TEMPLATE_STAR_COST) {
      toast.error(`Not enough stars. You need ${TEMPLATE_STAR_COST} stars but have ${currentStars}.`);
      return;
    }

    setIsCreatingStory(true);
    toast.success("Your story is being created");
    navigate("/storyverse");

    const _apiClient = apiClient;
    const _templateId = template.id;
    const _userId = user.id;
    const _profileId = currentProfile?.id;
    const _selectedCharacterIds = [...selectedCharacterIds];

    (async () => {
      try {
        const formData = new FormData();
        formData.append("template_id", _templateId);
        formData.append("user_id", _userId);
        formData.append("target_duration", "90");
        if (_profileId) {
          formData.append("profile_id", _profileId);
        }
        _selectedCharacterIds.forEach((characterId) => {
          formData.append("characters", characterId);
        });

        const payload = await _apiClient.uploadFileToEndpoint("/createStory", formData);
        const isSuccess =
          payload?.status === true ||
          payload?.status === "success" ||
          payload?.success === true;

        if (!isSuccess) {
          throw new Error(payload?.error || payload?.detail || "Failed to create story");
        }
        const remainingStars = payload?.remaining_stars ?? payload?.data?.remaining_stars;
        if (remainingStars !== undefined) {
          updateUser((prev) => ({ ...prev, pixley_stars: remainingStars }));
        }
      } catch (error) {
        toast.error(error?.message || "Could not create story right now.");
      }
    })();
  };

  const isCreateDisabled =
    isCreatingStory ||
    selectedCharacterIds.length !== maxCharacters ||
    isTemplateLoading ||
    !template;

  return (
    <div className="storyverse-container pb-16">
      <section className="relative -mt-28 h-[78vh] min-h-[520px] w-full overflow-hidden md:-mt-32">
        {template ? (
          <>
            <img
              src={template.image_url || FALLBACK_IMAGE}
              alt={template.title || "Template"}
              className="absolute inset-0 h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/35 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          </>
        ) : (
          <div className="h-full w-full animate-pulse bg-gradient-to-r from-[#121a45] via-[#1b2660] to-[#121a45]" />
        )}

        <div className="absolute inset-0 z-10 mx-auto flex w-full max-w-7xl items-end px-4 pb-10 sm:px-8 md:pb-14 lg:px-12">
          <div className="max-w-3xl">
            <p className="inline-flex rounded-full bg-violet-500/35 px-3 py-1 text-xs font-semibold text-violet-100">
              {template?.category || "Template"}
            </p>
            <h1 className="mt-3 text-3xl font-black leading-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] sm:text-5xl md:text-6xl">
              {template?.title || "Loading template..."}
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-white/90 drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)] sm:text-base md:text-lg">
              {template?.description || "Choose your characters to start this story."}
            </p>
            {templateError && (
              <p className="mt-4 rounded-xl border border-rose-500/40 bg-rose-900/30 px-4 py-2 text-sm text-rose-100">
                {templateError}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto mt-8 max-w-5xl space-y-6 px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Select Characters</h2>
            <p className="mt-1 text-xs text-white/55 sm:text-sm">
              This story needs exactly {maxCharacters} character{maxCharacters === 1 ? '' : 's'}.
            </p>
          </div>
          <p
            className={`text-sm font-semibold ${
              atSelectionLimit ? "text-violet-300" : "text-white/70"
            }`}
          >
            {selectedCharacterIds.length} of {maxCharacters} selected
          </p>
        </div>

        {isCharactersLoading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {[...Array(4)].map((_, index) => (
              <div
                key={`character-skeleton-${index}`}
                className="h-36 animate-pulse rounded-2xl border border-white/10 bg-white/5"
              />
            ))}
          </div>
        )}

        {!isCharactersLoading && charactersError && (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-900/20 p-4 text-rose-100">
            <p>{charactersError}</p>
            <button
              type="button"
              onClick={loadCharacters}
              className="mt-3 rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              Retry
            </button>
          </div>
        )}

        {!isCharactersLoading && !charactersError && characters.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center">
            <p className="text-white">No characters found. Create one to continue.</p>
            <button
              type="button"
              onClick={() => navigate("/create-character")}
              className="mt-4 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-700"
            >
              Create Character
            </button>
          </div>
        )}

        {!isCharactersLoading && !charactersError && characters.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {characters.map((character) => {
              const characterId = String(character?.id);
              const isSelected = selectedIdSet.has(characterId);
              const isLocked = !isSelected && atSelectionLimit;

              return (
                <motion.button
                  key={characterId}
                  type="button"
                  whileTap={isLocked ? undefined : { scale: 0.97 }}
                  onClick={() => toggleCharacterSelection(characterId)}
                  aria-disabled={isLocked}
                  className={`relative overflow-hidden rounded-2xl border p-2 text-left transition ${
                    isSelected
                      ? "border-violet-400 bg-violet-500/20"
                      : isLocked
                      ? "cursor-not-allowed border-white/10 bg-white/5 opacity-40"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <div className="w-full overflow-hidden rounded-xl aspect-[3/4]">
                    <img
                      src={
                        character?.avatar_url ||
                        character?.animated_image_url ||
                        character?.handdrawn_image_url ||
                        character?.image_url ||
                        FALLBACK_IMAGE
                      }
                      alt={character?.name || "Character"}
                      className="h-full w-full object-cover object-top"
                      onError={(event) => {
                        event.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <p className="mt-2 truncate text-sm font-semibold text-white">
                    {character?.name || "Unnamed Character"}
                  </p>
                  {isSelected && (
                    <span className="absolute right-3 top-3 rounded-full bg-violet-500 px-2 py-0.5 text-xs font-bold text-white">
                      Selected
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={handleCreateStory}
          disabled={isCreateDisabled}
          className="w-full rounded-2xl bg-violet-600 px-5 py-3 text-base font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-violet-600/45"
        >
          {isCreatingStory ? "Creating Story..." : "Create Story"}
        </button>
      </section>

    </div>
  );
  };

export default TemplateDetailPage;
